import React, { useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Form, Modal, Spinner } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import Header from "../../layouts/Header";
import axiosInstance, { opsAxiosInstance } from "../../api/axios";
// Reusable outlet dropdown (grouped Active / Inactive / Coming Soon).
// Adjust the path below to wherever OutletSelect actually lives in your tree.
import OutletSelect from "../../components/OutletSelect";
import LocationPickerModal from "../../components/LocationPickerModal";

// Hardcoded per request. Note: since this ships inside the frontend bundle,
// it is visible to anyone via browser dev tools / the network tab — treat
// it as public, not secret, and make sure this route is otherwise gated
// (VPN, login wall, etc.) if that matters for this deployment.
const API_KEY = "sYQ8SawD0mdqOtPO18NGs1paiEccyXJpFzsCiz1wXgC";
const API_SECRET = "79SvSQbGhcgCK9GdKTN61xTjiJvelIJVkC7pY7ioPaAOryyqbjy4pir2ckssjoCD";

// Attached to every admin request below.
const ADMIN_HEADERS = {
    headers: {
        "X-API-Key": API_KEY,
        "X-API-Secret": API_SECRET,
    },
};

const ENDPOINTS = {
    outlets: "/api/admin/outlets",
    staff: "/api/admin/staff",
    categories: "/api/admin/checklist-categories",
    tasks: "/api/admin/checklist-tasks",
    assignments: "/api/admin/outlet-checklist-categories",
};

const INITIAL_FORMS = {
    outlets: {
        outlet_name: "",
        outlet_code: "",
        address: "",
        city: "",
        state: "",
        latitude: "",
        longitude: "",
        is_active: "1",
    },
    staff: {
        outlet_id: "",
        staff_name: "",
        mobile_number: "",
        is_active: "1",
    },
    categories: {
        category_name: "",
        checklist_type: "OPENING",
        frequency: "DAILY",
        is_active: "1",
        sort_order: "",
    },
    tasks: {
        category_id: "",
        task_name: "",
        is_mandatory: "1",
        is_active: "1",
        sort_order: "",
        // Create-only helper: paste many tasks, one per line.
        bulk: false,
        bulk_text: "",
    },
    assignments: {
        outlet_id: "",
        category_id: "", // used when editing a single assignment
        category_ids: [], // used when creating (one outlet -> many categories)
        is_active: "1",
    },
};

const blankForm = (section) => ({ ...INITIAL_FORMS[section] });

const SECTION_LABEL = {
    outlets: "outlet",
    staff: "staff member",
    categories: "category",
    tasks: "task",
    assignments: "assignment",
};

const SECTION_HINT = {
    outlets: "Add an outlet that will run checklists.",
    staff: "Add a staff member and link them to an outlet.",
    categories: "Group related tasks, e.g. Opening checks.",
    tasks: "Tasks show up under their category at every assigned outlet.",
    assignments: "Choose which checklist categories an outlet runs.",
};

const TABS = [
    { key: "outlets", label: "Outlets", icon: "bi-shop", tone: "blue" },
    { key: "staff", label: "Staff", icon: "bi-person", tone: "purple" },
    { key: "categories", label: "Categories", icon: "bi-tag", tone: "orange" },
    { key: "tasks", label: "Tasks", icon: "bi-clipboard-check", tone: "green" },
    { key: "assignments", label: "Assignments", icon: "bi-link-45deg", tone: "indigo" },
];

const OUTLET_PAGE_SIZE = 12;

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const idOf = (x) => x?.id ?? x?._id;
const sid = (x) => String(idOf(x));
// The API is inconsistent about 1 / "1" / true — treat them all the same.
const isOn = (v) => v === true || String(v) === "1";
const flag = (v) => (isOn(v) ? "1" : "0");

const normalizeData = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data?.items)) return payload.data.items;
    return [];
};

// Maps a "main outlet" record (from getMainOutletsData / the `outlet` API)
// onto the checklist-admin outlet form shape. Field names on the left are
// based on what OutletSelect itself reads (_id, name, city, status) — if
// your outlet API returns address/state/lat/lng under different keys,
// adjust the right-hand side accordingly.
const mapMainOutletToForm = (outlet) => ({
    outlet_name: outlet?.name || outlet?.outlet_name || "",
    outlet_code: outlet?.code || outlet?.outlet_code || "",
    address: outlet?.address || "",
    city: outlet?.city || "",
    state: outlet?.state || "",
    latitude: String(outlet?.coordinates?.latitude ?? outlet?.lat ?? ""),
    longitude: String(outlet?.coordinates?.longitude ?? outlet?.lng ?? ""),
    is_active: outlet?.status === "ACTIVE" ? "1" : "0",
});

// Keeps only the last 10 digits so "+91 98765 43210" prefills cleanly.
const cleanMobile = (v) => String(v ?? "").replace(/\D/g, "").slice(-10);

// Maps an "outlet user" record (from getOutletUsers / the `outlet/user` API)
// onto the checklist-admin staff form shape. Field names on the left are a
// best guess (name, mobile_number, outlet_id, status) — verify against the
// real outletUsers response and adjust as needed.
const mapOutletUserToForm = (user) => ({
    outlet_id: String(user?.outlet_id || user?.outletId || ""),
    staff_name: user?.name || user?.staff_name || user?.full_name || "",
    mobile_number: cleanMobile(user?.mobile_number || user?.mobile || user?.phone || ""),
    is_active: user?.status === "ACTIVE" || isOn(user?.is_active) ? "1" : "0",
});

// react-select styling shared by every searchable dropdown in the forms.
const rsStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 38,
        fontSize: 14,
        borderRadius: 8,
        borderColor: state.selectProps.invalid ? "#dc3545" : state.isFocused ? "#3a57e8" : "#d0d5dd",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(58,87,232,.15)" : "none",
        "&:hover": { borderColor: state.isFocused ? "#3a57e8" : "#98a2b3" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 10px" }),
    placeholder: (base) => ({ ...base, color: "#98a2b3" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, borderRadius: 8, overflow: "hidden" }),
    option: (base, state) => ({
        ...base,
        fontSize: 13.5,
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        backgroundColor: state.isSelected ? "#3a57e8" : state.isFocused ? "#eef2ff" : "#fff",
        color: state.isSelected ? "#fff" : state.isDisabled ? "#98a2b3" : "#1d2939",
    }),
    multiValue: (base) => ({ ...base, backgroundColor: "#eef2ff", borderRadius: 6 }),
    multiValueLabel: (base) => ({ ...base, color: "#3a57e8", fontWeight: 600, fontSize: 12.5 }),
    multiValueRemove: (base) => ({ ...base, color: "#3a57e8", ":hover": { backgroundColor: "#dbe3ff", color: "#2441c9" } }),
};

const rsCommon = {
    styles: rsStyles,
    menuPortalTarget: typeof document !== "undefined" ? document.body : undefined,
    menuPosition: "fixed",
};

// ─── Small presentational pieces ───────────────────────────────────────────

const StatusPill = ({ active }) => (
    <span className={`co-pill ${active ? "is-active" : "is-inactive"}`}>
        {active ? "Active" : "Inactive"}
    </span>
);

// Icon-only row actions used by the table views.
const IconActions = ({ onEdit, onToggle, onDelete, isActive }) => (
    <div className="co-ibtns">
        <button type="button" className="co-ibtn" title="Edit" aria-label="Edit" onClick={onEdit}>
            <i className="bi bi-pencil-square" />
        </button>
        <button type="button" className="co-ibtn" title={isActive ? "Disable" : "Enable"} aria-label={isActive ? "Disable" : "Enable"} onClick={onToggle}>
            <i className={`bi ${isActive ? "bi-toggle-on" : "bi-toggle-off"}`} />
        </button>
        <button type="button" className="co-ibtn is-danger" title="Delete" aria-label="Delete" onClick={onDelete}>
            <i className="bi bi-trash3" />
        </button>
    </div>
);

// Outlet photo if the API provides one, otherwise a lettered tile.
const OutletThumb = ({ outlet, size = 64 }) => {
    const [broken, setBroken] = useState(false);
    console.log("Each Outlet :", outlet);
    
    const src = outlet?.image_url || outlet?.image || outlet?.logo || outlet?.photo;
    const initial = (outlet?.outlet_name || "?").trim().charAt(0).toUpperCase();
    return (
        <div className="co-thumb" style={{ width: size, height: size }}>
            {src && !broken ? (
                <img src={src} alt="" onError={() => setBroken(true)} />
            ) : (
                <span style={{ fontSize: size * 0.38 }}>{initial}</span>
            )}
        </div>
    );
};

const Field = ({ label, htmlFor, required, hint, error, warn, className = "", children }) => (
    <div className={`co-field ${className}`}>
        {label && (
            <label className="co-label" htmlFor={htmlFor}>
                {label}
                {required && <span className="co-req">*</span>}
            </label>
        )}
        {children}
        {error ? (
            <div className="co-err"><i className="bi bi-exclamation-circle" /> {error}</div>
        ) : warn ? (
            <div className="co-warn"><i className="bi bi-exclamation-triangle" /> {warn}</div>
        ) : hint ? (
            <div className="co-hint-txt">{hint}</div>
        ) : null}
    </div>
);

// Click-to-pick pills — much quicker than opening a <select> for 2–4 options.
const Segmented = ({ value, onChange, options }) => (
    <div className="co-seg" role="radiogroup">
        {options.map((o) => {
            const on = String(value) === String(o.value);
            return (
                <button
                    key={o.value}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    className={on ? "is-on" : ""}
                    onClick={() => onChange(o.value)}
                >
                    {o.icon && <i className={`bi ${o.icon}`} />}
                    {o.label}
                </button>
            );
        })}
    </div>
);

const StatusField = ({ value, onChange }) => (
    <Field label="Status">
        <Segmented
            value={value}
            onChange={onChange}
            options={[
                { value: "1", label: "Active", icon: "bi-check-circle" },
                { value: "0", label: "Inactive", icon: "bi-slash-circle" },
            ]}
        />
    </Field>
);

const FormSection = ({ title, children }) => (
    <div className="co-fsec">
        {title && <div className="co-fsec-title">{title}</div>}
        {children}
    </div>
);

// ─── Validation (runs live; errors only show after a field is touched or
// the user tries to save) ───────────────────────────────────────────────────
const validate = (section, f, ctx) => {
    const e = {};
    const isEdit = ctx.mode === "edit";

    if (section === "outlets") {
        if (!f.outlet_name.trim()) e.outlet_name = "Outlet name is required";
        const code = f.outlet_code.trim().toLowerCase();
        if (code && ctx.outlets.some((o) => sid(o) !== String(ctx.activeItemId) && String(o?.outlet_code || "").trim().toLowerCase() === code)) {
            e.outlet_code = "This outlet code is already in use";
        }
        const hasLat = String(f.latitude).trim() !== "";
        const hasLng = String(f.longitude).trim() !== "";
        if (hasLat && (Number.isNaN(Number(f.latitude)) || Math.abs(Number(f.latitude)) > 90)) e.latitude = "Must be between -90 and 90";
        if (hasLng && (Number.isNaN(Number(f.longitude)) || Math.abs(Number(f.longitude)) > 180)) e.longitude = "Must be between -180 and 180";
        if (hasLat && !hasLng && !e.longitude) e.longitude = "Enter longitude too";
        if (hasLng && !hasLat && !e.latitude) e.latitude = "Enter latitude too";
    }

    if (section === "staff") {
        if (!f.outlet_id) e.outlet_id = "Select an outlet";
        if (!f.staff_name.trim()) e.staff_name = "Staff name is required";
        if (!f.mobile_number) e.mobile_number = "Mobile number is required";
        else if (!/^\d{10}$/.test(f.mobile_number)) e.mobile_number = "Enter a 10-digit mobile number";
    }

    if (section === "categories") {
        if (!f.category_name.trim()) e.category_name = "Category name is required";
        if (f.sort_order !== "" && !/^\d+$/.test(String(f.sort_order))) e.sort_order = "Use a whole number";
    }

    if (section === "tasks") {
        if (!f.category_id) e.category_id = "Select a category";
        if (f.bulk && !isEdit) {
            if (!f.bulk_text.split("\n").some((l) => l.trim())) e.bulk_text = "Add at least one task";
        } else if (!f.task_name.trim()) {
            e.task_name = "Task name is required";
        }
        if (f.sort_order !== "" && !/^\d+$/.test(String(f.sort_order))) e.sort_order = "Use a whole number";
    }

    if (section === "assignments") {
        if (!f.outlet_id) e.outlet_id = "Select an outlet";
        if (isEdit) {
            if (!f.category_id) e.category_id = "Select a category";
        } else if (!f.category_ids.length) {
            e.category_ids = "Select at least one category";
        }
    }

    return e;
};

const ChecklistOperations = () => {

    const accessToken = localStorage.getItem("accessToken");
    const [activeTab, setActiveTab] = useState("outlets");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [outlets, setOutlets] = useState([]);
    const [staff, setStaff] = useState([]);
    const [categories, setCategories] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [assignments, setAssignments] = useState([]);

    // One form object for whichever section the modal is on.
    const [form, setForm] = useState(blankForm("outlets"));
    const [touched, setTouched] = useState({});
    const [submitted, setSubmitted] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState("create");
    const [modalSection, setModalSection] = useState("outlets");
    const [activeItemId, setActiveItemId] = useState(null);

    // Google Maps picker for the outlet form's latitude/longitude fields.
    const [showMapModal, setShowMapModal] = useState(false);

    // Confirm-delete modal state — replaces window.confirm with something
    // that actually matches the rest of the UI and names what's being removed.
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteBusy, setDeleteBusy] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    // Kept separate on purpose: reusing one filter value for both the staff
    // tab (filters by outlet_id) and the tasks tab (filters by category_id)
    // meant switching tabs silently applied the wrong filter, since the two
    // id spaces can overlap.
    const [staffOutletFilter, setStaffOutletFilter] = useState("all");
    const [taskCategoryFilter, setTaskCategoryFilter] = useState("all");

    // Outlet cards are paginated client-side so a deployment with ~100
    // outlets doesn't render/scroll all of them at once.
    const [outletPage, setOutletPage] = useState(1);
    const [outletView, setOutletView] = useState("grid");
    const [expanded, setExpanded] = useState({});

    const [internalOutlets, setInternalOutlets] = useState([]);

    // Currently selected "existing outlet" chosen in the Add Outlet modal,
    // used purely to prefill the form. Not submitted to the API directly.
    const [selectedMainOutlet, setSelectedMainOutlet] = useState(null);
    const [internalOutletUsers, setInternalOutletUsers] = useState([]);

    // Currently selected "existing outlet user" chosen in the Add Staff
    // modal, used purely to prefill the form. Not submitted directly.
    const [selectedMainStaff, setSelectedMainStaff] = useState(null);

    useEffect(() => {
        loadAllData();
        getMainOutletsData();
        getOutletUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reset filters whenever the tab changes so state from one tab never
    // silently leaks into another.
    useEffect(() => {
        setSearchTerm("");
        setStatusFilter("all");
        setStaffOutletFilter("all");
        setTaskCategoryFilter("all");
        setOutletPage(1);
    }, [activeTab]);

    // Jump back to page 1 whenever the outlet search/status filter changes,
    // so a filter that narrows the list doesn't leave the user stranded on
    // a now-empty page.
    useEffect(() => {
        setOutletPage(1);
    }, [searchTerm, statusFilter]);

    const getMainOutletsData = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`
            }
            const response = await axiosInstance.get('outlet', { headers })
            if (response?.data?.status === true) {
                setInternalOutlets(response.data.data.outlets);
            }
        } catch (error) {
            if (error?.response) {
                const status = error?.response?.status;
                let errorMessage = status === 406
                    ? error?.response?.data?.error?.message || error?.response?.data?.error
                    : status === 412
                        ? error?.response?.data?.data?.message
                        : error?.response?.data?.error || 'Failed to fetch outlets';
                toast.error(errorMessage, { position: 'top-right', autoClose: 3000, theme: 'colored' });
            }
        }
    }

    const getOutletUsers = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get(`outlet/user`, { headers });
            if (response?.data?.status === true) {
                const users = response?.data?.data?.outletUsers;
                setInternalOutletUsers(users);
            }
        } catch (error) {
            if (error.response?.data?.status === false)
                toast.error(error.response.data.error);
        }
    };

    // `silent` refreshes in the background (after save/toggle/delete) so the
    // page doesn't flash a spinner or lose its scroll position.
    const loadAllData = async (silent = false) => {
        if (!silent) setLoading(true);

        try {
            const requests = [
                opsAxiosInstance.get(ENDPOINTS.outlets, ADMIN_HEADERS),
                opsAxiosInstance.get(ENDPOINTS.staff, ADMIN_HEADERS),
                opsAxiosInstance.get(ENDPOINTS.categories, ADMIN_HEADERS),
                opsAxiosInstance.get(ENDPOINTS.tasks, ADMIN_HEADERS),
                opsAxiosInstance.get(ENDPOINTS.assignments, ADMIN_HEADERS),
            ];

            const [outletsRes, staffRes, categoriesRes, tasksRes, assignmentsRes] = await Promise.all(requests);

            setOutlets(normalizeData(outletsRes?.data));
            setStaff(normalizeData(staffRes?.data));
            setCategories(normalizeData(categoriesRes?.data));
            setTasks(normalizeData(tasksRes?.data));
            setAssignments(normalizeData(assignmentsRes?.data));
        } catch (error) {
            const message = error?.response?.data?.message || "Unable to load checklist admin data.";
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    // ─── Derived lookups ────────────────────────────────────────────────────
    const staffByOutlet = useMemo(() => {
        const map = {};
        staff.forEach((item) => {
            const key = String(item?.outlet_id);
            if (!map[key]) map[key] = [];
            map[key].push(item);
        });
        return map;
    }, [staff]);

    const assignmentsByOutletId = useMemo(() => {
        const map = {};
        assignments.forEach((item) => {
            const key = String(item?.outlet_id);
            if (!map[key]) map[key] = [];
            map[key].push(item);
        });
        return map;
    }, [assignments]);

    const tasksByCategoryId = useMemo(() => {
        const map = {};
        tasks.forEach((item) => {
            const key = String(item?.category_id);
            if (!map[key]) map[key] = [];
            map[key].push(item);
        });
        return map;
    }, [tasks]);

    const categoryById = useMemo(() => {
        const map = {};
        categories.forEach((item) => {
            map[sid(item)] = item;
        });
        return map;
    }, [categories]);

    const getOutletStats = (outlet) => {
        const key = sid(outlet);
        const outletStaff = staffByOutlet[key] || [];
        const outletAssignments = assignmentsByOutletId[key] || [];
        const uniqueCategoryIds = [...new Set(outletAssignments.map((a) => String(a?.category_id)))];
        const outletTasks = uniqueCategoryIds.flatMap((cid) => tasksByCategoryId[cid] || []);
        return { outletStaff, outletAssignments, outletTasks, categoryCount: uniqueCategoryIds.length };
    };

    // Options for the searchable selects inside the forms.
    const outletOptions = useMemo(
        () => outlets.map((o) => ({
            value: sid(o),
            label: o?.outlet_name || "Unnamed outlet",
            meta: [o?.outlet_code, o?.city].filter(Boolean).join(" · "),
        })),
        [outlets]
    );

    const categoryOptions = useMemo(
        () => categories.map((c) => ({
            value: sid(c),
            label: c?.category_name || "Unnamed category",
            meta: [c?.checklist_type, c?.frequency].filter(Boolean).join(" · "),
        })),
        [categories]
    );

    // Options for the "prefill from existing outlet user" react-select in
    // the Add Staff modal, grouped by outlet so it's easy to scan when the
    // same person appears at multiple outlets. Adjust the field names below
    // (name / mobile_number / outlet_name) once the real outletUsers shape
    // is confirmed.
    const staffUserOptions = useMemo(() => {
        const groups = {};
        internalOutletUsers.forEach((user) => {
            const outletLabel = user?.outlet_name || user?.outletName || "Other";
            if (!groups[outletLabel]) groups[outletLabel] = [];
            groups[outletLabel].push({
                value: user?._id || user?.id,
                label: `${user?.name || user?.staff_name || "Unnamed"}${user?.mobile_number ? ` · ${user.mobile_number}` : ""}`,
            });
        });
        return Object.entries(groups).map(([outletLabel, options]) => ({
            label: outletLabel,
            options,
        }));
    }, [internalOutletUsers]);

    // Next free sort order, so the person entering data never has to look it up.
    const nextSortOrder = (list) =>
        list.reduce((max, item) => Math.max(max, Number(item?.sort_order) || 0), 0) + 1;

    const suggestedSort = useMemo(() => {
        if (modalSection === "categories") return nextSortOrder(categories);
        if (modalSection === "tasks") return nextSortOrder(tasks.filter((t) => String(t?.category_id) === String(form.category_id)));
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalSection, categories, tasks, form.category_id]);

    // ─── Form helpers ───────────────────────────────────────────────────────
    const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));
    const markTouched = (name) => setTouched((prev) => ({ ...prev, [name]: true }));

    const errors = useMemo(
        () => (showModal ? validate(modalSection, form, { mode: modalMode, activeItemId, outlets }) : {}),
        [showModal, modalSection, form, modalMode, activeItemId, outlets]
    );
    const errOf = (name) => (submitted || touched[name] ? errors[name] : undefined);

    // Spread onto a Form.Control: value, change, blur, error state, and an id
    // the validator can focus.
    const bind = (name, transform) => ({
        id: `f-${name}`,
        value: form[name],
        onChange: (e) => setField(name, transform ? transform(e.target.value) : e.target.value),
        onBlur: () => markTouched(name),
        isInvalid: !!errOf(name),
    });

    const focusField = (name) => {
        setTimeout(() => {
            const el = document.getElementById(`f-${name}`);
            if (el) {
                el.focus();
                el.scrollIntoView?.({ block: "center", behavior: "smooth" });
            }
        }, 30);
    };

    const resetFormState = () => {
        setTouched({});
        setSubmitted(false);
        setActiveItemId(null);
        setSelectedMainOutlet(null);
        setSelectedMainStaff(null);
    };

    // Which field should get the cursor when the modal opens.
    const firstFieldFor = (section, mode, f) => {
        if (section === "outlets") return "outlet_name";
        if (section === "staff") return f.outlet_id ? "staff_name" : "outlet_id";
        if (section === "categories") return "category_name";
        if (section === "tasks") return f.category_id ? "task_name" : "category_id";
        if (section === "assignments") return f.outlet_id ? (mode === "edit" ? "category_id" : "category_ids") : "outlet_id";
        return null;
    };

    const openCreateModal = (section, preset = {}) => {
        resetFormState();
        setForm({ ...blankForm(section), ...preset });
        setModalSection(section);
        setModalMode("create");
        setShowModal(true);
    };

    // Same as openCreateModal, but pre-fills the outlet so adding staff or
    // assigning a category from an outlet card doesn't make the user pick
    // the outlet a second time.
    const openAddStaffForOutlet = (outlet) => openCreateModal("staff", { outlet_id: sid(outlet) });

    const openAddAssignmentForOutlet = (outlet) => openCreateModal("assignments", { outlet_id: sid(outlet) });

    // Tasks belong to a category, not directly to an outlet, so this
    // pre-fills the category with the outlet's first assigned category
    // when there is exactly one unambiguous choice — otherwise it leaves
    // the category picker for the admin to fill in.
    const openAddTaskForOutlet = (outlet) => {
        const outletAssignments = assignmentsByOutletId[sid(outlet)] || [];
        const uniqueCategoryIds = [...new Set(outletAssignments.map((a) => String(a?.category_id)))];
        const defaultCategoryId = uniqueCategoryIds.length === 1 ? uniqueCategoryIds[0] : "";

        if (outletAssignments.length === 0) {
            toast.warning("This outlet has no assigned categories yet. Assign one first, or pick a category manually.");
        }
        openCreateModal("tasks", { category_id: defaultCategoryId });
    };

    const openEditModal = (section, item) => {
        resetFormState();
        setModalSection(section);
        setModalMode("edit");
        setActiveItemId(idOf(item));

        if (section === "outlets") {
            setForm({
                outlet_name: item?.outlet_name || "",
                outlet_code: item?.outlet_code || "",
                address: item?.address || "",
                city: item?.city || "",
                state: item?.state || "",
                latitude: String(item?.latitude ?? ""),
                longitude: String(item?.longitude ?? ""),
                is_active: flag(item?.is_active ?? 1),
            });
        }

        if (section === "staff") {
            setForm({
                outlet_id: String(item?.outlet_id || ""),
                staff_name: item?.staff_name || "",
                mobile_number: cleanMobile(item?.mobile_number),
                is_active: flag(item?.is_active ?? 1),
            });
        }

        if (section === "categories") {
            setForm({
                category_name: item?.category_name || "",
                checklist_type: item?.checklist_type || "OPENING",
                frequency: item?.frequency || "DAILY",
                is_active: flag(item?.is_active ?? 1),
                sort_order: String(item?.sort_order ?? ""),
            });
        }

        if (section === "tasks") {
            setForm({
                ...blankForm("tasks"),
                category_id: String(item?.category_id || ""),
                task_name: item?.task_name || "",
                is_mandatory: flag(item?.is_mandatory ?? 1),
                is_active: flag(item?.is_active ?? 1),
                sort_order: String(item?.sort_order ?? ""),
            });
        }

        if (section === "assignments") {
            setForm({
                ...blankForm("assignments"),
                outlet_id: String(item?.outlet_id || ""),
                category_id: String(item?.category_id || ""),
                is_active: flag(item?.is_active ?? 1),
            });
        }

        setShowModal(true);
    };

    const closeModal = () => {
        if (saving) return;
        setShowModal(false);
        resetFormState();
    };

    // Fires when the admin picks an outlet from the "prefill from existing
    // outlet" dropdown inside the Add Outlet modal. Looks up the full record
    // in internalOutlets (fetched by getMainOutletsData) and maps it onto
    // the form. Clearing the select resets the form back to blank.
    const handleSelectMainOutlet = (selectedOption) => {
        setSelectedMainOutlet(selectedOption);
        setTouched({});

        if (!selectedOption) {
            setForm(blankForm("outlets"));
            return;
        }

        const matched = internalOutlets.find((o) => o._id === selectedOption.value);
        if (matched) {
            setForm(mapMainOutletToForm(matched));
            focusField("outlet_code");
        } else {
            toast.warning("Couldn't find full details for that outlet — please fill the form in manually.");
        }
    };

    // Fires when a location is confirmed in the map picker modal — writes the
    // chosen lat/lng straight into the form, same pattern as AddOutlet.js.
    const handleLocationConfirm = ({ lat, lng }) => {
        setForm((prev) => ({
            ...prev,
            latitude: lat.toFixed(6),
            longitude: lng.toFixed(6),
        }));
    };

    // Fires when the admin picks a user from the "prefill from existing
    // outlet user" react-select dropdown inside the Add Staff modal. Looks
    // up the full record in internalOutletUsers (fetched by getOutletUsers)
    // and maps it onto the form. Clearing the select resets the form.
    const handleSelectMainStaff = (selectedOption) => {
        setSelectedMainStaff(selectedOption);
        setTouched({});

        if (!selectedOption) {
            setForm(blankForm("staff"));
            return;
        }

        const matched = internalOutletUsers.find(
            (u) => (u._id || u.id) === selectedOption.value
        );
        if (matched) {
            setForm(mapOutletUserToForm(matched));
        } else {
            toast.warning("Couldn't find full details for that user — please fill the form in manually.");
        }
    };

    const buildPayload = (section, f) => {
        if (section === "outlets") {
            return {
                outlet_name: f.outlet_name.trim(),
                outlet_code: f.outlet_code.trim() || undefined,
                address: f.address.trim() || undefined,
                city: f.city.trim() || undefined,
                state: f.state.trim() || undefined,
                latitude: String(f.latitude).trim() !== "" ? Number(f.latitude) : undefined,
                longitude: String(f.longitude).trim() !== "" ? Number(f.longitude) : undefined,
                is_active: Number(f.is_active),
            };
        }
        if (section === "staff") {
            return {
                outlet_id: Number(f.outlet_id),
                staff_name: f.staff_name.trim(),
                mobile_number: f.mobile_number,
                is_active: Number(f.is_active),
            };
        }
        if (section === "categories") {
            return {
                category_name: f.category_name.trim(),
                checklist_type: f.checklist_type,
                frequency: f.frequency,
                is_active: Number(f.is_active),
                sort_order: f.sort_order !== "" ? Number(f.sort_order) : undefined,
            };
        }
        if (section === "tasks") {
            return {
                category_id: Number(f.category_id),
                task_name: f.task_name.trim(),
                is_mandatory: Number(f.is_mandatory),
                is_active: Number(f.is_active),
                sort_order: f.sort_order !== "" ? Number(f.sort_order) : undefined,
            };
        }
        return {
            outlet_id: Number(f.outlet_id),
            category_id: Number(f.category_id),
            is_active: Number(f.is_active),
        };
    };

    // What to keep when the person chooses "Save & add another" — the fields
    // that are usually the same for the next entry.
    const carryOver = (section, f) => {
        if (section === "staff") return { outlet_id: f.outlet_id };
        if (section === "categories") return { checklist_type: f.checklist_type, frequency: f.frequency };
        if (section === "tasks") return { category_id: f.category_id, is_mandatory: f.is_mandatory, bulk: f.bulk };
        if (section === "assignments") return { outlet_id: f.outlet_id };
        return {};
    };

    const submit = async (event, addAnother = false) => {
        event?.preventDefault?.();
        if (saving) return;

        setSubmitted(true);
        const currentErrors = validate(modalSection, form, { mode: modalMode, activeItemId, outlets });
        const firstBad = Object.keys(currentErrors)[0];
        if (firstBad) {
            focusField(firstBad);
            return;
        }

        const base = ENDPOINTS[modalSection];
        let total = 1;
        let created = 0;
        setSaving(true);

        try {
            if (modalMode === "edit") {
                await opsAxiosInstance.put(`${base}/${activeItemId}`, buildPayload(modalSection, form), ADMIN_HEADERS);
                toast.success(`${cap(SECTION_LABEL[modalSection])} updated.`);
            } else if (modalSection === "tasks" && form.bulk) {
                const lines = [...new Set(form.bulk_text.split("\n").map((l) => l.trim()).filter(Boolean))];
                total = lines.length;
                const start = form.sort_order !== "" ? Number(form.sort_order) : suggestedSort;
                for (const [index, line] of lines.entries()) {
                    await opsAxiosInstance.post(
                        base,
                        buildPayload("tasks", { ...form, task_name: line, sort_order: String(start + index) }),
                        ADMIN_HEADERS
                    );
                    created += 1;
                }
                toast.success(`${created} task${created === 1 ? "" : "s"} added.`);
            } else if (modalSection === "assignments") {
                const already = new Set(
                    assignments.filter((a) => String(a?.outlet_id) === String(form.outlet_id)).map((a) => String(a?.category_id))
                );
                const todo = form.category_ids.filter((cid) => !already.has(String(cid)));
                if (todo.length === 0) {
                    toast.info("Those categories are already assigned to this outlet.");
                    setSaving(false);
                    return;
                }
                total = todo.length;
                for (const cid of todo) {
                    await opsAxiosInstance.post(
                        base,
                        buildPayload("assignments", { ...form, category_id: cid }),
                        ADMIN_HEADERS
                    );
                    created += 1;
                }
                toast.success(`${created} categor${created === 1 ? "y" : "ies"} assigned.`);
            } else {
                await opsAxiosInstance.post(base, buildPayload(modalSection, form), ADMIN_HEADERS);
                toast.success(`${cap(SECTION_LABEL[modalSection])} added.`);
            }

            loadAllData(true);

            if (addAnother && modalMode === "create") {
                const next = { ...blankForm(modalSection), ...carryOver(modalSection, form) };
                setForm(next);
                setTouched({});
                setSubmitted(false);
                setSelectedMainOutlet(null);
                setSelectedMainStaff(null);
                focusField(firstFieldFor(modalSection, "create", next));
            } else {
                setShowModal(false);
                resetFormState();
            }
        } catch (error) {
            const message = error?.response?.data?.message || error?.response?.data?.errors?.[0] || "Unable to save the selected checklist item.";
            if (created > 0) {
                toast.error(`Saved ${created} of ${total}. ${message}`);
                loadAllData(true);
            } else {
                toast.error(message);
            }
        } finally {
            setSaving(false);
        }
    };

    // Human-readable label for the confirm-delete dialog, per section.
    const getItemLabel = (section, item) => {
        if (section === "outlets") return item?.outlet_name || "this outlet";
        if (section === "staff") return item?.staff_name || "this staff member";
        if (section === "categories") return item?.category_name || "this category";
        if (section === "tasks") return item?.task_name || "this task";
        if (section === "assignments") {
            const parts = [item?.category_name, item?.outlet_name].filter(Boolean);
            return parts.length ? `${parts[0]}${parts[1] ? ` for ${parts[1]}` : ""}` : "this assignment";
        }
        return "this item";
    };

    // Opens the confirm-delete modal instead of deleting immediately.
    // Takes the full item (not just its id) so the dialog can name what's
    // about to be removed.
    const requestDelete = (section, item) => {
        setDeleteTarget({
            section,
            id: idOf(item),
            label: getItemLabel(section, item),
        });
    };

    const cancelDelete = () => {
        if (deleteBusy) return;
        setDeleteTarget(null);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        const { section, id } = deleteTarget;
        setDeleteBusy(true);

        try {
            await opsAxiosInstance.delete(`${ENDPOINTS[section]}/${id}`, ADMIN_HEADERS);
            toast.success("Removed successfully.");
            setDeleteTarget(null);
            loadAllData(true);
        } catch (error) {
            const message = error?.response?.data?.message || "Unable to delete this item.";
            toast.error(message);
        } finally {
            setDeleteBusy(false);
        }
    };

    const toggleStatus = async (section, item) => {
        const targetId = idOf(item);
        const nextValue = isOn(item?.is_active) ? "0" : "1";

        try {
            await opsAxiosInstance.put(`${ENDPOINTS[section]}/${targetId}`, { is_active: nextValue }, ADMIN_HEADERS);
            toast.success("Status updated.");
            loadAllData(true);
        } catch (error) {
            const message = error?.response?.data?.message || "Unable to update status.";
            toast.error(message);
        }
    };

    const toggleExpanded = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

    // ─── Filtering ──────────────────────────────────────────────────────────
    const q = searchTerm.trim().toLowerCase();
    const statusOk = (item) => (statusFilter === "all" ? true : String(flag(item?.is_active)) === statusFilter);

    const filteredOutlets = useMemo(() => {
        return outlets.filter((item) => {
            const matchesSearch = [item?.outlet_name, item?.outlet_code, item?.city, item?.state].filter(Boolean).join(" ").toLowerCase().includes(q);
            return matchesSearch && statusOk(item);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outlets, q, statusFilter]);

    const totalOutletPages = Math.max(1, Math.ceil(filteredOutlets.length / OUTLET_PAGE_SIZE));
    const safeOutletPage = Math.min(outletPage, totalOutletPages);
    const pagedOutlets = useMemo(() => {
        const start = (safeOutletPage - 1) * OUTLET_PAGE_SIZE;
        return filteredOutlets.slice(start, start + OUTLET_PAGE_SIZE);
    }, [filteredOutlets, safeOutletPage]);

    const filteredStaff = useMemo(() => {
        return staff.filter((item) => {
            const matchesSearch = [item?.staff_name, item?.mobile_number, item?.outlet_name].filter(Boolean).join(" ").toLowerCase().includes(q);
            const matchesOutlet = staffOutletFilter === "all" ? true : String(item?.outlet_id) === staffOutletFilter;
            return matchesSearch && statusOk(item) && matchesOutlet;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [staff, q, statusFilter, staffOutletFilter]);

    const filteredCategories = useMemo(() => {
        return categories.filter((item) => {
            const matchesSearch = [item?.category_name, item?.checklist_type, item?.frequency].filter(Boolean).join(" ").toLowerCase().includes(q);
            return matchesSearch && statusOk(item);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categories, q, statusFilter]);

    const filteredTasks = useMemo(() => {
        return tasks.filter((item) => {
            const matchesSearch = [item?.task_name, item?.category_name].filter(Boolean).join(" ").toLowerCase().includes(q);
            const matchesCategory = taskCategoryFilter === "all" ? true : String(item?.category_id) === taskCategoryFilter;
            return matchesSearch && statusOk(item) && matchesCategory;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tasks, q, statusFilter, taskCategoryFilter]);

    const filteredAssignments = useMemo(() => {
        return assignments.filter((item) => {
            const matchesSearch = [item?.outlet_name, item?.category_name].filter(Boolean).join(" ").toLowerCase().includes(q);
            return matchesSearch && statusOk(item);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignments, q, statusFilter]);

    const counts = {
        outlets: outlets.length,
        staff: staff.length,
        categories: categories.length,
        tasks: tasks.length,
        assignments: assignments.length,
    };

    const setupSteps = [
        { label: "Outlets", tab: "outlets", done: counts.outlets > 0 },
        { label: "Categories & Tasks", tab: "categories", done: counts.categories > 0 && counts.tasks > 0 },
        { label: "Outlet Assignments", tab: "assignments", done: counts.assignments > 0 },
        { label: "Staff", tab: "staff", done: counts.staff > 0 },
    ];

    // ─── Form bodies ────────────────────────────────────────────────────────
    const optionLabel = (o, { context }) => (
        <div className="co-opt">
            <span>{o.label}</span>
            {o.meta && context === "menu" && <small>{o.meta}</small>}
        </div>
    );

    const selectOutlet = (name, { placeholder = "Select outlet" } = {}) => (
        <Select
            {...rsCommon}
            inputId={`f-${name}`}
            options={outletOptions}
            value={outletOptions.find((o) => o.value === String(form[name])) || null}
            onChange={(opt) => { setField(name, opt?.value || ""); markTouched(name); }}
            onBlur={() => markTouched(name)}
            placeholder={placeholder}
            formatOptionLabel={optionLabel}
            invalid={!!errOf(name)}
            noOptionsMessage={() => "No outlets found"}
        />
    );

    const renderFormBody = () => {
        const isEdit = modalMode === "edit";

        if (modalSection === "outlets") {
            return (
                <>
                    {!isEdit && (
                        <div className="co-prefill">
                            <OutletSelect
                                label="Prefill from existing outlet"
                                placeholder="Search outlets to prefill this form..."
                                value={selectedMainOutlet}
                                onChange={handleSelectMainOutlet}
                                outlets={internalOutlets}
                                isClearable
                                required={false}
                            />
                        </div>
                    )}

                    <FormSection title="Basic details">
                        <div className="co-grid co-grid-3">
                            <Field label="Outlet name" htmlFor="f-outlet_name" required error={errOf("outlet_name")} className="co-span-2">
                                <Form.Control placeholder="e.g. Calangute Outlet" autoComplete="off" {...bind("outlet_name")} />
                            </Field>
                            <Field label="Outlet code" htmlFor="f-outlet_code" error={errOf("outlet_code")}>
                                <Form.Control placeholder="e.g. CAL01" autoComplete="off" {...bind("outlet_code")} />
                            </Field>
                        </div>
                        <StatusField value={form.is_active} onChange={(v) => setField("is_active", v)} />
                    </FormSection>

                    <FormSection title="Location">
                        <div className="co-grid co-grid-2">
                            <Field label="City" htmlFor="f-city">
                                <Form.Control placeholder="City" autoComplete="off" {...bind("city")} />
                            </Field>
                            <Field label="State" htmlFor="f-state">
                                <Form.Control placeholder="State" autoComplete="off" {...bind("state")} />
                            </Field>
                        </div>
                        <Field label="Address" htmlFor="f-address">
                            <Form.Control as="textarea" rows={2} placeholder="Street, area, landmark" {...bind("address")} />
                        </Field>
                        <div className="co-grid co-grid-3 co-coords">
                            <Field label="Latitude" htmlFor="f-latitude" error={errOf("latitude")}>
                                <Form.Control type="number" step="0.000001" placeholder="15.498200" {...bind("latitude")} />
                            </Field>
                            <Field label="Longitude" htmlFor="f-longitude" error={errOf("longitude")}>
                                <Form.Control type="number" step="0.000001" placeholder="73.828000" {...bind("longitude")} />
                            </Field>
                            <Field label=" ">
                                <Button variant="outline-primary" className="co-btn w-100" onClick={() => setShowMapModal(true)}>
                                    <i className="bi bi-geo-alt" /> {form.latitude && form.longitude ? "Change on map" : "Pick on map"}
                                </Button>
                            </Field>
                        </div>
                    </FormSection>
                </>
            );
        }

        if (modalSection === "staff") {
            const dupMobile = form.mobile_number.length === 10 && form.outlet_id && staff.some(
                (s) => sid(s) !== String(activeItemId) && String(s?.outlet_id) === String(form.outlet_id) && cleanMobile(s?.mobile_number) === form.mobile_number
            );
            return (
                <>
                    {!isEdit && (
                        <div className="co-prefill">
                            <div className="co-prefill-title"><i className="bi bi-person-badge" /> Quick start — copy from an existing outlet user</div>
                            <Select
                                {...rsCommon}
                                isClearable
                                isSearchable
                                placeholder="Search staff/outlet users..."
                                value={selectedMainStaff}
                                onChange={handleSelectMainStaff}
                                options={staffUserOptions}
                                noOptionsMessage={() => "No outlet users found"}
                            />
                        </div>
                    )}
                    <FormSection>
                        <Field label="Outlet" htmlFor="f-outlet_id" required error={errOf("outlet_id")}>
                            {selectOutlet("outlet_id")}
                        </Field>
                        <div className="co-grid co-grid-2">
                            <Field label="Staff name" htmlFor="f-staff_name" required error={errOf("staff_name")}>
                                <Form.Control placeholder="Full name" autoComplete="off" {...bind("staff_name")} />
                            </Field>
                            <Field
                                label="Mobile number"
                                htmlFor="f-mobile_number"
                                required
                                error={errOf("mobile_number")}
                                warn={dupMobile ? "A staff member with this number already exists at this outlet" : undefined}
                                hint={`${form.mobile_number.length}/10 digits`}
                            >
                                <Form.Control
                                    type="tel"
                                    inputMode="numeric"
                                    placeholder="10-digit number"
                                    autoComplete="off"
                                    {...bind("mobile_number", (v) => v.replace(/\D/g, "").slice(0, 10))}
                                />
                            </Field>
                        </div>
                        <StatusField value={form.is_active} onChange={(v) => setField("is_active", v)} />
                    </FormSection>
                </>
            );
        }

        if (modalSection === "categories") {
            return (
                <FormSection>
                    <Field label="Category name" htmlFor="f-category_name" required error={errOf("category_name")}>
                        <Form.Control placeholder="e.g. Kitchen opening checks" autoComplete="off" {...bind("category_name")} />
                    </Field>
                    <Field label="Checklist type">
                        <Segmented
                            value={form.checklist_type}
                            onChange={(v) => setField("checklist_type", v)}
                            options={[
                                { value: "OPENING", label: "Opening", icon: "bi-sunrise" },
                                { value: "CLOSING", label: "Closing", icon: "bi-moon-stars" },
                                { value: "GENERAL", label: "General", icon: "bi-list-check" },
                            ]}
                        />
                    </Field>
                    <Field label="Frequency">
                        <Segmented
                            value={form.frequency}
                            onChange={(v) => setField("frequency", v)}
                            options={[
                                { value: "DAILY", label: "Daily" },
                                { value: "WEEKLY", label: "Weekly" },
                                { value: "BIWEEKLY", label: "Bi-weekly" },
                                { value: "MONTHLY", label: "Monthly" },
                            ]}
                        />
                    </Field>
                    <div className="co-grid co-grid-2">
                        <Field label="Sort order" htmlFor="f-sort_order" error={errOf("sort_order")} hint="Leave blank to let the system decide">
                            <Form.Control type="number" min="0" placeholder={suggestedSort ? `Suggested: ${suggestedSort}` : "Sort order"} {...bind("sort_order")} />
                        </Field>
                        <StatusField value={form.is_active} onChange={(v) => setField("is_active", v)} />
                    </div>
                </FormSection>
            );
        }

        if (modalSection === "tasks") {
            const inCategory = tasks.filter((t) => String(t?.category_id) === String(form.category_id));
            const dupName = !form.bulk && form.task_name.trim() && inCategory.some(
                (t) => sid(t) !== String(activeItemId) && String(t?.task_name || "").trim().toLowerCase() === form.task_name.trim().toLowerCase()
            );
            const bulkLines = [...new Set(form.bulk_text.split("\n").map((l) => l.trim()).filter(Boolean))];
            const bulkDupes = bulkLines.filter((l) => inCategory.some((t) => String(t?.task_name || "").trim().toLowerCase() === l.toLowerCase()));
            return (
                <FormSection>
                    <Field
                        label="Category"
                        htmlFor="f-category_id"
                        required
                        error={errOf("category_id")}
                        hint={form.category_id ? `${inCategory.length} existing task${inCategory.length === 1 ? "" : "s"} in this category` : undefined}
                    >
                        <Select
                            {...rsCommon}
                            inputId="f-category_id"
                            options={categoryOptions}
                            value={categoryOptions.find((o) => o.value === String(form.category_id)) || null}
                            onChange={(opt) => { setField("category_id", opt?.value || ""); markTouched("category_id"); }}
                            onBlur={() => markTouched("category_id")}
                            placeholder="Select category"
                            formatOptionLabel={optionLabel}
                            invalid={!!errOf("category_id")}
                            noOptionsMessage={() => "No categories found"}
                        />
                    </Field>

                    {!isEdit && (
                        <Segmented
                            value={form.bulk ? "multi" : "single"}
                            onChange={(v) => setField("bulk", v === "multi")}
                            options={[
                                { value: "single", label: "One task", icon: "bi-plus-circle" },
                                { value: "multi", label: "Add many at once", icon: "bi-list-ul" },
                            ]}
                        />
                    )}

                    {form.bulk && !isEdit ? (
                        <Field
                            label="Tasks (one per line)"
                            htmlFor="f-bulk_text"
                            required
                            error={errOf("bulk_text")}
                            warn={bulkDupes.length ? `Already in this category: ${bulkDupes.slice(0, 3).join(", ")}${bulkDupes.length > 3 ? "…" : ""}` : undefined}
                            hint={bulkLines.length ? `${bulkLines.length} task${bulkLines.length === 1 ? "" : "s"} will be added · Ctrl+Enter to save` : "Paste or type each task on its own line"}
                        >
                            <Form.Control as="textarea" rows={6} placeholder={"Switch on all lights\nCheck gas cylinder\nClean the counter"} {...bind("bulk_text")} />
                        </Field>
                    ) : (
                        <Field
                            label="Task name"
                            htmlFor="f-task_name"
                            required
                            error={errOf("task_name")}
                            warn={dupName ? "A task with this name already exists in this category" : undefined}
                        >
                            <Form.Control placeholder="e.g. Switch on all lights" autoComplete="off" {...bind("task_name")} />
                        </Field>
                    )}

                    <div className="co-grid co-grid-2">
                        <Field label="Mandatory">
                            <Segmented
                                value={form.is_mandatory}
                                onChange={(v) => setField("is_mandatory", v)}
                                options={[
                                    { value: "1", label: "Required", icon: "bi-asterisk" },
                                    { value: "0", label: "Optional" },
                                ]}
                            />
                        </Field>
                        <StatusField value={form.is_active} onChange={(v) => setField("is_active", v)} />
                    </div>

                    <Field label="Sort order" htmlFor="f-sort_order" error={errOf("sort_order")} hint={form.bulk && !isEdit ? "Numbers continue from here for each line" : "Leave blank to let the system decide"}>
                        <Form.Control type="number" min="0" className="co-narrow" placeholder={suggestedSort && form.category_id ? `Suggested: ${suggestedSort}` : "Sort order"} {...bind("sort_order")} />
                    </Field>
                </FormSection>
            );
        }

        if (modalSection === "assignments") {
            const assignedSet = new Set(
                assignments
                    .filter((a) => String(a?.outlet_id) === String(form.outlet_id) && sid(a) !== String(activeItemId))
                    .map((a) => String(a?.category_id))
            );
            const unassigned = categoryOptions.filter((o) => !assignedSet.has(o.value));
            return (
                <FormSection>
                    <Field label="Outlet" htmlFor="f-outlet_id" required error={errOf("outlet_id")}>
                        {selectOutlet("outlet_id")}
                    </Field>

                    {isEdit ? (
                        <Field label="Checklist category" htmlFor="f-category_id" required error={errOf("category_id")}>
                            <Select
                                {...rsCommon}
                                inputId="f-category_id"
                                options={categoryOptions}
                                isOptionDisabled={(o) => assignedSet.has(o.value)}
                                value={categoryOptions.find((o) => o.value === String(form.category_id)) || null}
                                onChange={(opt) => { setField("category_id", opt?.value || ""); markTouched("category_id"); }}
                                onBlur={() => markTouched("category_id")}
                                placeholder="Select category"
                                formatOptionLabel={optionLabel}
                                invalid={!!errOf("category_id")}
                            />
                        </Field>
                    ) : (
                        <Field
                            label="Checklist categories"
                            htmlFor="f-category_ids"
                            required
                            error={errOf("category_ids")}
                            hint={form.outlet_id && assignedSet.size ? `${assignedSet.size} already assigned to this outlet (greyed out)` : "Pick several to assign them all in one go"}
                        >
                            <Select
                                {...rsCommon}
                                isMulti
                                closeMenuOnSelect={false}
                                inputId="f-category_ids"
                                options={categoryOptions}
                                isOptionDisabled={(o) => assignedSet.has(o.value)}
                                value={categoryOptions.filter((o) => form.category_ids.includes(o.value))}
                                onChange={(opts) => { setField("category_ids", (opts || []).map((o) => o.value)); markTouched("category_ids"); }}
                                onBlur={() => markTouched("category_ids")}
                                placeholder="Select categories"
                                formatOptionLabel={optionLabel}
                                invalid={!!errOf("category_ids")}
                                noOptionsMessage={() => "No categories found"}
                            />
                            {unassigned.length > 1 && (
                                <button
                                    type="button"
                                    className="co-linkbtn"
                                    onClick={() => setField("category_ids", unassigned.map((o) => o.value))}
                                >
                                    Select all {unassigned.length} unassigned
                                </button>
                            )}
                        </Field>
                    )}

                    <StatusField value={form.is_active} onChange={(v) => setField("is_active", v)} />
                </FormSection>
            );
        }

        return null;
    };

    // ─── Outlet details (staff / assignments / tasks) ───────────────────────
    const renderOutletDetails = (outlet, wide = false) => {
        const { outletStaff, outletAssignments, outletTasks } = getOutletStats(outlet);
        return (
            <div className={`co-details ${wide ? "is-wide" : ""}`}>
                <div className="co-section">
                    <div className="co-section-head">
                        <span>Staff<span className="co-section-count">{outletStaff.length}</span></span>
                        <button type="button" className="co-link" onClick={() => openAddStaffForOutlet(outlet)}>+ Add</button>
                    </div>
                    <div className="co-section-list">
                        {outletStaff.length === 0 && <div className="co-section-empty">No staff assigned</div>}
                        {outletStaff.map((s) => (
                            <div key={sid(s)} className="co-section-row">
                                <span>{s?.staff_name}</span>
                                <span className="co-section-sub">{s?.mobile_number}</span>
                                <button type="button" className="co-row-delete" title="Remove staff" aria-label="Remove staff" onClick={() => requestDelete("staff", s)}>×</button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="co-section">
                    <div className="co-section-head">
                        <span>Assignments<span className="co-section-count">{outletAssignments.length}</span></span>
                        <button type="button" className="co-link" onClick={() => openAddAssignmentForOutlet(outlet)}>+ Assign</button>
                    </div>
                    <div className="co-section-list">
                        {outletAssignments.length === 0 && <div className="co-section-empty">No assignments yet</div>}
                        {outletAssignments.map((a) => {
                            const cat = categoryById[String(a?.category_id)];
                            const detail = [cat?.checklist_type, cat?.frequency].filter(Boolean).join(" · ");
                            return (
                                <div key={sid(a)} className="co-section-row">
                                    <span>{a?.category_name || cat?.category_name || "—"}</span>
                                    <span className="co-section-sub">{detail}</span>
                                    <button type="button" className="co-row-delete" title="Remove assignment" aria-label="Remove assignment" onClick={() => requestDelete("assignments", a)}>×</button>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="co-section">
                    <div className="co-section-head">
                        <span>Tasks<span className="co-section-count">{outletTasks.length}</span></span>
                        <div className="co-section-head-actions">
                            <button type="button" className="co-link" onClick={() => openAddTaskForOutlet(outlet)}>+ Add</button>
                            <span className="co-sep">·</span>
                            <button type="button" className="co-link" onClick={() => setActiveTab("tasks")}>View all</button>
                        </div>
                    </div>
                    <div className="co-section-list co-section-list-tall">
                        {outletTasks.length === 0 && <div className="co-section-empty">No tasks available</div>}
                        {outletTasks.map((t) => {
                            const cat = categoryById[String(t?.category_id)];
                            return (
                                <div key={sid(t)} className="co-section-row">
                                    <span>{t?.task_name}</span>
                                    <span className="co-section-sub">
                                        {cat?.category_name || t?.category_name || ""}
                                        {isOn(t?.is_mandatory) ? " · Required" : ""}
                                    </span>
                                    <button type="button" className="co-row-delete" title="Remove task" aria-label="Remove task" onClick={() => requestDelete("tasks", t)}>×</button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    const locationText = (o) => [o?.city, o?.state].filter(Boolean).join(", ");

    const renderOutletCard = (outlet) => {
        const id = sid(outlet);
        const active = isOn(outlet?.is_active);
        const { outletStaff, outletAssignments, outletTasks, categoryCount } = getOutletStats(outlet);
        const open = !!expanded[id];
        const tiles = [
            { label: "Staff", value: outletStaff.length, icon: "bi-person" },
            { label: "Categories", value: categoryCount, icon: "bi-grid" },
            { label: "Tasks", value: outletTasks.length, icon: "bi-clipboard-check" },
            { label: "Assignments", value: outletAssignments.length, icon: "bi-link-45deg" },
        ];

        return (
            <div key={id} className={`co-oc ${active ? "" : "is-off"}`}>
                <div className="co-oc-top">
                    <OutletThumb outlet={outlet} />
                    <div className="co-oc-info">
                        <div className="co-oc-row">
                            <div className="co-oc-name" title={outlet?.outlet_name}>{outlet?.outlet_name}</div>
                            <StatusPill active={active} />
                            <Dropdown align="end">
                                <Dropdown.Toggle variant="link" className="co-kebab" aria-label="More actions">
                                    <i className="bi bi-three-dots-vertical" />
                                </Dropdown.Toggle>
                                <Dropdown.Menu renderOnMount className="co-menu">
                                    <Dropdown.Item onClick={() => openEditModal("outlets", outlet)}><i className="bi bi-pencil-square" /> Edit outlet</Dropdown.Item>
                                    <Dropdown.Item onClick={() => toggleStatus("outlets", outlet)}>
                                        <i className={`bi ${active ? "bi-toggle-off" : "bi-toggle-on"}`} /> {active ? "Disable" : "Enable"}
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item className="text-danger" onClick={() => requestDelete("outlets", outlet)}><i className="bi bi-trash3" /> Delete</Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                        <div className="co-oc-loc">
                            <i className="bi bi-geo-alt" />
                            <span>{locationText(outlet) || "No location added"}</span>
                            {outlet?.outlet_code && <span className="co-oc-code">{outlet.outlet_code}</span>}
                        </div>
                    </div>
                </div>

                <div className="co-oc-tiles">
                    {tiles.map((t) => (
                        <div key={t.label} className="co-oc-tile">
                            <i className={`bi ${t.icon}`} />
                            <div>
                                <div className="co-oc-tile-label">{t.label}</div>
                                <div className="co-oc-tile-value">{t.value}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <button type="button" className="co-oc-toggle" aria-expanded={open} onClick={() => toggleExpanded(id)}>
                    <i className={`bi ${open ? "bi-chevron-down" : "bi-chevron-right"}`} />
                    {open ? "Hide details" : "View details"}
                </button>
                {open && renderOutletDetails(outlet)}
            </div>
        );
    };

    const renderOutletList = () => (
        <div className="co-tablewrap">
            <table className="co-table">
                <thead>
                    <tr>
                        <th style={{ width: 36 }} />
                        <th>Outlet</th>
                        <th>Location</th>
                        <th className="text-center">Staff</th>
                        <th className="text-center">Categories</th>
                        <th className="text-center">Tasks</th>
                        <th className="text-center">Assignments</th>
                        <th>Status</th>
                        <th className="text-end">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {pagedOutlets.map((outlet) => {
                        const id = sid(outlet);
                        const active = isOn(outlet?.is_active);
                        const { outletStaff, outletAssignments, outletTasks, categoryCount } = getOutletStats(outlet);
                        const open = !!expanded[id];
                        return (
                            <React.Fragment key={id}>
                                <tr>
                                    <td>
                                        <button type="button" className="co-ibtn" aria-label={open ? "Hide details" : "View details"} aria-expanded={open} onClick={() => toggleExpanded(id)}>
                                            <i className={`bi ${open ? "bi-chevron-down" : "bi-chevron-right"}`} />
                                        </button>
                                    </td>
                                    <td>
                                        <div className="co-cell-outlet">
                                            <OutletThumb outlet={outlet} size={34} />
                                            <div>
                                                <div className="co-cell-name">{outlet?.outlet_name}</div>
                                                {outlet?.outlet_code && <div className="co-cell-sub">{outlet.outlet_code}</div>}
                                            </div>
                                        </div>
                                    </td>
                                    <td>{locationText(outlet) || "—"}</td>
                                    <td className="text-center">{outletStaff.length}</td>
                                    <td className="text-center">{categoryCount}</td>
                                    <td className="text-center">{outletTasks.length}</td>
                                    <td className="text-center">{outletAssignments.length}</td>
                                    <td><StatusPill active={active} /></td>
                                    <td className="text-end">
                                        <IconActions
                                            isActive={active}
                                            onEdit={() => openEditModal("outlets", outlet)}
                                            onToggle={() => toggleStatus("outlets", outlet)}
                                            onDelete={() => requestDelete("outlets", outlet)}
                                        />
                                    </td>
                                </tr>
                                {open && (
                                    <tr className="co-detail-row">
                                        <td colSpan={9}>{renderOutletDetails(outlet, true)}</td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    const renderEmpty = (tab, message) => (
        <div className="co-empty-block">
            <div className="co-empty-icon"><i className={`bi ${TABS.find((t) => t.key === tab)?.icon}`} /></div>
            <div className="co-empty-title">{message}</div>
            {(searchTerm || statusFilter !== "all" || staffOutletFilter !== "all" || taskCategoryFilter !== "all") ? (
                <button type="button" className="co-linkbtn" onClick={() => { setSearchTerm(""); setStatusFilter("all"); setStaffOutletFilter("all"); setTaskCategoryFilter("all"); }}>
                    Clear filters
                </button>
            ) : (
                <Button size="sm" className="co-btn co-btn-primary" onClick={() => openCreateModal(tab)}>
                    <i className="bi bi-plus-lg" /> Add {SECTION_LABEL[tab]}
                </Button>
            )}
        </div>
    );

    const renderTable = (head, rows, emptyTab, emptyMsg) => (
        rows.length === 0 ? renderEmpty(emptyTab, emptyMsg) : (
            <div className="co-tablewrap">
                <table className="co-table">
                    <thead>
                        <tr>
                            {head.map((h) => <th key={h} className={h === "Actions" ? "text-end" : ""}>{h}</th>)}
                        </tr>
                    </thead>
                    <tbody>{rows}</tbody>
                </table>
            </div>
        )
    );

    const ADD_LABEL = {
        outlets: "Add Outlet",
        staff: "Add Staff",
        categories: "Add Category",
        tasks: "Add Task",
        assignments: "Add Assignment",
    };

    const SEARCH_PLACEHOLDER = {
        outlets: "Search outlets...",
        staff: "Search staff...",
        categories: "Search categories...",
        tasks: "Search tasks...",
        assignments: "Search assignments...",
    };

    return (
        <React.Fragment>
            <Header />
            <style>{coStyles}</style>
            <div className="main main-app co-page">

                {/* ── Banner ── */}
                <div className="co-banner">
                    <div className="co-banner-left">
                        <div className="co-banner-icon"><i className="bi bi-clipboard-check" /></div>
                        <div>
                            <div className="co-eyebrow">Checklist operations</div>
                            <h4 className="co-title">Management</h4>
                            <p className="co-subtitle">Manage outlets, staff, checklist categories, tasks and outlet assignments.</p>
                        </div>
                    </div>
                    <div className="co-banner-art" aria-hidden="true">
                        <div className="co-art-sheet"><span /><span /><span /><span /></div>
                    </div>
                    <Button className="co-btn co-btn-primary co-banner-btn" onClick={() => openCreateModal(activeTab)}>
                        <i className="bi bi-plus-lg" /> {ADD_LABEL[activeTab]}
                    </Button>
                </div>

                {/* ── Setup order ── */}
                {/* <div className="co-setup">
                    <div className="co-setup-label"><i className="bi bi-gear" /> Recommended setup order:</div>
                    <div className="co-steps">
                        {setupSteps.map((s, i) => (
                            <React.Fragment key={s.label}>
                                {i > 0 && <i className="bi bi-arrow-right co-step-arrow" />}
                                <button type="button" className={`co-step ${s.done ? "is-done" : ""}`} onClick={() => setActiveTab(s.tab)}>
                                    <span className="co-step-num">{s.done ? <i className="bi bi-check-lg" /> : i + 1}</span>
                                    {s.label}
                                </button>
                            </React.Fragment>
                        ))}
                    </div>
                </div> */}

                {/* ── Stat cards ── */}
                <div className="co-stats">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            className={`co-stat tone-${t.tone} ${activeTab === t.key ? "is-active" : ""}`}
                            onClick={() => setActiveTab(t.key)}
                        >
                            <div className="co-stat-icon"><i className={`bi ${t.icon}`} /></div>
                            <div className="co-stat-body">
                                <div className="co-stat-value">{counts[t.key]}</div>
                                <div className="co-stat-label">{t.label}</div>
                            </div>
                            <i className="bi bi-chevron-right co-stat-chev" />
                        </button>
                    ))}
                </div>

                {/* ── Main panel ── */}
                <div className="co-panel">
                    <div className="co-toolbar">
                        <div className="co-tabs" role="tablist">
                            {TABS.map((t) => (
                                <button
                                    key={t.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === t.key}
                                    className={activeTab === t.key ? "is-active" : ""}
                                    onClick={() => setActiveTab(t.key)}
                                >
                                    <i className={`bi ${t.icon}`} /> {t.label}
                                </button>
                            ))}
                        </div>

                        <div className="co-filters">
                            <div className="co-searchbox">
                                <i className="bi bi-search" />
                                <input
                                    type="text"
                                    placeholder={SEARCH_PLACEHOLDER[activeTab]}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    aria-label="Search"
                                />
                                {searchTerm && (
                                    <button type="button" aria-label="Clear search" onClick={() => setSearchTerm("")}><i className="bi bi-x-lg" /></button>
                                )}
                            </div>
                            <Form.Select className="co-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status filter">
                                <option value="all">All status</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </Form.Select>
                            {activeTab === "staff" && (
                                <Form.Select className="co-select" value={staffOutletFilter} onChange={(e) => setStaffOutletFilter(e.target.value)} aria-label="Outlet filter">
                                    <option value="all">All outlets</option>
                                    {outlets.map((item) => (<option key={sid(item)} value={sid(item)}>{item?.outlet_name}</option>))}
                                </Form.Select>
                            )}
                            {activeTab === "tasks" && (
                                <Form.Select className="co-select" value={taskCategoryFilter} onChange={(e) => setTaskCategoryFilter(e.target.value)} aria-label="Category filter">
                                    <option value="all">All categories</option>
                                    {categories?.map((item) => (<option key={sid(item)} value={sid(item)}>{item?.category_name}</option>))}
                                </Form.Select>
                            )}
                            {activeTab === "outlets" && (
                                <div className="co-viewtoggle" role="group" aria-label="View mode">
                                    <button type="button" className={outletView === "grid" ? "is-on" : ""} title="Grid view" aria-label="Grid view" onClick={() => setOutletView("grid")}><i className="bi bi-grid-fill" /></button>
                                    <button type="button" className={outletView === "list" ? "is-on" : ""} title="List view" aria-label="List view" onClick={() => setOutletView("list")}><i className="bi bi-list-ul" /></button>
                                </div>
                            )}
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>
                    ) : (
                        <>
                            {activeTab === "outlets" && (
                                filteredOutlets.length === 0 ? (
                                    renderEmpty("outlets", outlets.length === 0 ? "No outlets yet" : "No outlets match the current filters.")
                                ) : (
                                    <>
                                        {outletView === "grid" ? (
                                            <div className="co-outlet-grid">{pagedOutlets.map(renderOutletCard)}</div>
                                        ) : (
                                            renderOutletList()
                                        )}

                                        {filteredOutlets.length > OUTLET_PAGE_SIZE && (
                                            <div className="co-pagination">
                                                <span className="co-pagination-info">
                                                    Showing {(safeOutletPage - 1) * OUTLET_PAGE_SIZE + 1}
                                                    –{Math.min(safeOutletPage * OUTLET_PAGE_SIZE, filteredOutlets.length)} of {filteredOutlets.length} outlets
                                                </span>
                                                <div className="co-pagination-controls">
                                                    <Button variant="outline-secondary" size="sm" className="co-btn" disabled={safeOutletPage <= 1} onClick={() => setOutletPage((p) => Math.max(1, p - 1))}>
                                                        Prev
                                                    </Button>
                                                    <span className="co-pagination-page">Page {safeOutletPage} of {totalOutletPages}</span>
                                                    <Button variant="outline-secondary" size="sm" className="co-btn" disabled={safeOutletPage >= totalOutletPages} onClick={() => setOutletPage((p) => Math.min(totalOutletPages, p + 1))}>
                                                        Next
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )
                            )}

                            {activeTab === "staff" && renderTable(
                                ["Name", "Mobile", "Outlet", "Status", "Actions"],
                                filteredStaff.map((item) => (
                                    <tr key={sid(item)}>
                                        <td className="co-cell-name">{item?.staff_name}</td>
                                        <td>{item?.mobile_number}</td>
                                        <td>{item?.outlet_name || "—"}</td>
                                        <td><StatusPill active={isOn(item?.is_active)} /></td>
                                        <td className="text-end">
                                            <IconActions
                                                isActive={isOn(item?.is_active)}
                                                onEdit={() => openEditModal("staff", item)}
                                                onToggle={() => toggleStatus("staff", item)}
                                                onDelete={() => requestDelete("staff", item)}
                                            />
                                        </td>
                                    </tr>
                                )),
                                "staff",
                                staff.length === 0 ? "No staff yet" : "No staff match the current filters."
                            )}

                            {activeTab === "categories" && renderTable(
                                ["Name", "Type", "Frequency", "Status", "Actions"],
                                filteredCategories.map((item) => (
                                    <tr key={sid(item)}>
                                        <td className="co-cell-name">{item?.category_name}</td>
                                        <td><span className="co-tag">{item?.checklist_type}</span></td>
                                        <td><span className="co-tag is-soft">{item?.frequency}</span></td>
                                        <td><StatusPill active={isOn(item?.is_active)} /></td>
                                        <td className="text-end">
                                            <IconActions
                                                isActive={isOn(item?.is_active)}
                                                onEdit={() => openEditModal("categories", item)}
                                                onToggle={() => toggleStatus("categories", item)}
                                                onDelete={() => requestDelete("categories", item)}
                                            />
                                        </td>
                                    </tr>
                                )),
                                "categories",
                                categories.length === 0 ? "No categories yet" : "No categories match the current filters."
                            )}

                            {activeTab === "tasks" && renderTable(
                                ["Task", "Category", "Mandatory", "Status", "Actions"],
                                filteredTasks.map((item) => (
                                    <tr key={sid(item)}>
                                        <td className="co-cell-name">{item?.task_name}</td>
                                        <td>{item?.category_name || "—"}</td>
                                        <td>{isOn(item?.is_mandatory) ? <span className="co-tag">Required</span> : <span className="co-cell-sub">Optional</span>}</td>
                                        <td><StatusPill active={isOn(item?.is_active)} /></td>
                                        <td className="text-end">
                                            <IconActions
                                                isActive={isOn(item?.is_active)}
                                                onEdit={() => openEditModal("tasks", item)}
                                                onToggle={() => toggleStatus("tasks", item)}
                                                onDelete={() => requestDelete("tasks", item)}
                                            />
                                        </td>
                                    </tr>
                                )),
                                "tasks",
                                tasks.length === 0 ? "No tasks yet" : "No tasks match the current filters."
                            )}

                            {activeTab === "assignments" && renderTable(
                                ["Outlet", "Category", "Status", "Actions"],
                                filteredAssignments.map((item) => (
                                    <tr key={sid(item)}>
                                        <td className="co-cell-name">{item?.outlet_name || "—"}</td>
                                        <td>{item?.category_name || "—"}</td>
                                        <td><StatusPill active={isOn(item?.is_active)} /></td>
                                        <td className="text-end">
                                            <IconActions
                                                isActive={isOn(item?.is_active)}
                                                onEdit={() => openEditModal("assignments", item)}
                                                onToggle={() => toggleStatus("assignments", item)}
                                                onDelete={() => requestDelete("assignments", item)}
                                            />
                                        </td>
                                    </tr>
                                )),
                                "assignments",
                                assignments.length === 0 ? "No assignments yet" : "No assignments match the current filters."
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* ── Add / Edit modal ── */}
            <Modal
                show={showModal}
                onHide={closeModal}
                centered
                backdrop="static"
                size={modalSection === "outlets" ? "lg" : undefined}
                className="co-modal"
                onEntered={() => focusField(firstFieldFor(modalSection, modalMode, form))}
            >
                <Form
                    noValidate
                    onSubmit={(e) => submit(e, false)}
                    onKeyDown={(e) => {
                        if ((e.ctrlKey || e.metaKey) && e.key === "Enter") submit(e, false);
                    }}
                >
                    <Modal.Header closeButton>
                        <div className={`co-mh-icon tone-${TABS.find((t) => t.key === modalSection)?.tone}`}>
                            <i className={`bi ${TABS.find((t) => t.key === modalSection)?.icon}`} />
                        </div>
                        <div>
                            <Modal.Title className="co-modal-title">
                                {modalMode === "edit" ? "Edit" : "Add"} {SECTION_LABEL[modalSection]}
                            </Modal.Title>
                            <div className="co-modal-sub">{modalMode === "edit" ? "Update the details below and save." : SECTION_HINT[modalSection]}</div>
                        </div>
                    </Modal.Header>
                    <Modal.Body>
                        {renderFormBody()}
                        <div className="co-req-note"><span className="co-req">*</span> Required</div>
                    </Modal.Body>
                    <Modal.Footer>
                        <span className="co-foot-hint"><kbd>Enter</kbd> save · <kbd>Esc</kbd> close</span>
                        <Button variant="light" className="co-btn" onClick={closeModal} disabled={saving}>Cancel</Button>
                        {modalMode === "create" && (
                            <Button variant="outline-primary" className="co-btn" onClick={(e) => submit(e, true)} disabled={saving}>
                                Save &amp; add another
                            </Button>
                        )}
                        <Button type="submit" className="co-btn co-btn-primary" disabled={saving}>
                            {saving ? <Spinner animation="border" size="sm" /> : modalMode === "edit" ? "Save changes" : "Create"}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* ── Delete confirm ── */}
            <Modal show={!!deleteTarget} onHide={cancelDelete} centered size="sm" className="co-modal co-delete-modal">
                <Modal.Body className="text-center">
                    <div className="co-del-icon"><i className="bi bi-trash3" /></div>
                    <div className="co-modal-title mb-1">Delete {deleteTarget ? SECTION_LABEL[deleteTarget.section] : "item"}?</div>
                    <p className="co-delete-text">
                        This will permanently remove <strong>{deleteTarget?.label}</strong>. This action cannot be undone.
                    </p>
                    <div className="d-flex gap-2 justify-content-center">
                        <Button variant="light" className="co-btn" onClick={cancelDelete} disabled={deleteBusy}>Cancel</Button>
                        <Button variant="danger" className="co-btn" onClick={confirmDelete} disabled={deleteBusy}>
                            {deleteBusy ? <Spinner animation="border" size="sm" /> : "Delete"}
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            <LocationPickerModal
                show={showMapModal}
                onHide={() => setShowMapModal(false)}
                onConfirm={handleLocationConfirm}
                initialLat={form.latitude}
                initialLng={form.longitude}
            />

        </React.Fragment>
    );
};

const coStyles = `
  .co-page { --co-primary:#3a57e8; --co-primary-dark:#2b44c4; --co-ink:#1d2939; --co-muted:#667085; --co-line:#e4e7ec; padding: 18px 24px 40px; background:#f5f6fa; }

  /* Banner */
  .co-banner { position:relative; display:flex; align-items:center; gap:16px; padding:20px 24px; border-radius:10px; margin-bottom:14px; overflow:hidden; color:#fff; background:linear-gradient(110deg,#0d1b4c 0%,#16296b 55%,#1f3a8a 100%); }
  .co-banner::after { content:""; position:absolute; inset:0; background:radial-gradient(circle at 78% 20%, rgba(120,150,255,.28), transparent 45%); pointer-events:none; }
  .co-banner-left { position:relative; z-index:1; display:flex; align-items:center; gap:16px; flex:1; min-width:0; }
  .co-banner-icon { width:56px; height:56px; flex-shrink:0; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:24px; color:#fff; background:linear-gradient(135deg,#3a57e8,#5b78ff); box-shadow:0 8px 20px rgba(58,87,232,.45); }
  .co-eyebrow { font-size:11px; letter-spacing:.14em; text-transform:uppercase; color:#b9c6ff; font-weight:600; margin-bottom:2px; }
  .co-title { font-weight:700; color:#fff; margin:0 0 2px; font-size:24px; letter-spacing:-.3px; }
  .co-subtitle { color:#d3dbff; font-size:13px; margin:0; }
  .co-banner-art { position:relative; z-index:1; width:170px; height:70px; flex-shrink:0; }
  .co-art-sheet { position:absolute; right:10px; top:-14px; width:92px; height:100px; border-radius:8px; border:1px solid rgba(255,255,255,.25); background:linear-gradient(160deg,rgba(255,255,255,.22),rgba(255,255,255,.05)); padding:18px 14px; display:flex; flex-direction:column; gap:9px; transform:perspective(400px) rotateY(-14deg) rotateZ(4deg); }
  .co-art-sheet span { height:6px; border-radius:3px; background:rgba(200,214,255,.6); }
  .co-art-sheet span:nth-child(odd) { width:70%; }
  .co-banner-btn { position:relative; z-index:1; flex-shrink:0; }

  /* Buttons */
  .co-btn.btn { height:38px; padding:0 16px; font-size:13.5px; font-weight:600; border-radius:8px; display:inline-flex; align-items:center; justify-content:center; gap:7px; white-space:nowrap; }
  .co-btn-primary.btn { background:var(--co-primary); border-color:var(--co-primary); color:#fff; }
  .co-btn-primary.btn:hover, .co-btn-primary.btn:focus, .co-btn-primary.btn:active { background:var(--co-primary-dark) !important; border-color:var(--co-primary-dark) !important; color:#fff; }
  .co-btn-primary.btn:disabled { background:var(--co-primary); border-color:var(--co-primary); opacity:.6; }
  .co-btn.btn-outline-primary { color:var(--co-primary); border-color:#c7d2fe; }
  .co-btn.btn-outline-primary:hover { background:#eef2ff; color:var(--co-primary-dark); border-color:var(--co-primary); }
  .co-btn.btn-light { background:#f2f4f7; border-color:#f2f4f7; color:#344054; }
  .co-linkbtn { background:none; border:0; padding:0; margin-top:6px; font-size:12.5px; font-weight:600; color:var(--co-primary); cursor:pointer; }
  .co-linkbtn:hover { text-decoration:underline; }

  /* Setup order */
  .co-setup { display:flex; align-items:center; gap:14px; flex-wrap:wrap; margin-bottom:14px; }
  .co-setup-label { display:inline-flex; align-items:center; gap:8px; font-size:13px; color:var(--co-muted); background:#fff; border:1px solid var(--co-line); border-radius:8px; padding:10px 14px; }
  .co-steps { flex:1; display:flex; align-items:center; gap:10px; flex-wrap:wrap; background:#fff; border:1px solid var(--co-line); border-radius:8px; padding:8px 14px; min-width:0; }
  .co-step { display:inline-flex; align-items:center; gap:7px; border:0; background:none; font-size:12.5px; color:#344054; font-weight:500; padding:2px 0; cursor:pointer; }
  .co-step:hover { color:var(--co-primary); }
  .co-step-num { width:20px; height:20px; border-radius:50%; background:#e4e7ec; color:#475467; font-size:11px; font-weight:700; display:inline-flex; align-items:center; justify-content:center; }
  .co-step.is-done .co-step-num { background:#12b76a; color:#fff; }
  .co-step-arrow { color:#98a2b3; font-size:12px; }

  /* Stat cards */
  .co-stats { display:grid; grid-template-columns:repeat(5,minmax(0,1fr)); gap:12px; margin-bottom:14px; }
  .co-stat { display:flex; align-items:center; gap:12px; text-align:left; background:#fff; border:1px solid var(--co-line); border-radius:10px; padding:14px 14px; cursor:pointer; transition:box-shadow .15s, border-color .15s, transform .15s; }
  .co-stat:hover { box-shadow:0 4px 14px rgba(16,24,40,.08); transform:translateY(-1px); }
  .co-stat.is-active { border-color:var(--co-primary); box-shadow:0 0 0 1px var(--co-primary) inset; }
  .co-stat-icon { width:44px; height:44px; flex-shrink:0; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:20px; }
  .co-stat-body { flex:1; min-width:0; }
  .co-stat-value { font-size:22px; font-weight:700; color:var(--co-ink); line-height:1.1; }
  .co-stat-label { font-size:11px; text-transform:uppercase; letter-spacing:.06em; color:var(--co-muted); font-weight:600; margin-top:3px; }
  .co-stat-chev { color:#98a2b3; font-size:12px; }
  .tone-blue .co-stat-icon, .co-mh-icon.tone-blue { background:#e8eeff; color:#3a57e8; }
  .tone-purple .co-stat-icon, .co-mh-icon.tone-purple { background:#f1e8ff; color:#7c3aed; }
  .tone-orange .co-stat-icon, .co-mh-icon.tone-orange { background:#ffefdc; color:#ea8a00; }
  .tone-green .co-stat-icon, .co-mh-icon.tone-green { background:#dff7ea; color:#0f9d58; }
  .tone-indigo .co-stat-icon, .co-mh-icon.tone-indigo { background:#e5ecff; color:#4f46e5; }

  /* Panel + toolbar */
  .co-panel { background:#fff; border:1px solid var(--co-line); border-radius:10px; padding:6px 16px 16px; }
  .co-toolbar { display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; border-bottom:1px solid var(--co-line); margin:0 -16px 16px; padding:0 16px; }
  .co-tabs { display:flex; gap:4px; }
  .co-tabs button { display:inline-flex; align-items:center; gap:8px; border:0; background:none; padding:14px 14px; font-size:13.5px; font-weight:500; color:var(--co-muted); border-bottom:2px solid transparent; margin-bottom:-1px; white-space:nowrap; cursor:pointer; }
  .co-tabs button:hover { color:var(--co-ink); }
  .co-tabs button.is-active { color:var(--co-primary); font-weight:600; border-bottom-color:var(--co-primary); }
  .co-filters { display:flex; gap:8px; flex-wrap:wrap; align-items:center; padding:8px 0; }
  .co-searchbox { position:relative; width:240px; }
  .co-searchbox > i { position:absolute; left:11px; top:50%; transform:translateY(-50%); color:#98a2b3; font-size:13px; }
  .co-searchbox input { width:100%; height:38px; border:1px solid #d0d5dd; border-radius:8px; padding:0 30px 0 32px; font-size:13.5px; outline:none; background:#fff; }
  .co-searchbox input:focus { border-color:var(--co-primary); box-shadow:0 0 0 3px rgba(58,87,232,.15); }
  .co-searchbox button { position:absolute; right:6px; top:50%; transform:translateY(-50%); border:0; background:none; color:#98a2b3; font-size:11px; padding:4px; }
  .co-select.form-select { width:160px; height:38px; font-size:13.5px; border-radius:8px; border-color:#d0d5dd; }
  .co-viewtoggle { display:inline-flex; gap:6px; }
  .co-viewtoggle button { width:38px; height:38px; border:1px solid #d0d5dd; background:#fff; border-radius:8px; color:#667085; display:inline-flex; align-items:center; justify-content:center; }
  .co-viewtoggle button.is-on { background:var(--co-primary); border-color:var(--co-primary); color:#fff; }

  /* Outlet cards */
  .co-outlet-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:16px; align-items:start; }
  .co-oc { background:#fff; border:1px solid var(--co-line); border-radius:10px; padding:14px; transition:box-shadow .15s, border-color .15s; }
  .co-oc:hover { border-color:#c7d0e0; box-shadow:0 4px 14px rgba(16,24,40,.07); }
  .co-oc.is-off .co-thumb { filter:grayscale(.6); opacity:.8; }
  .co-oc-top { display:flex; gap:14px; align-items:flex-start; }
  .co-thumb { flex-shrink:0; border-radius:8px; overflow:hidden; background:linear-gradient(135deg,#e8eeff,#d5defc); display:flex; align-items:center; justify-content:center; color:var(--co-primary); font-weight:700; }
  .co-thumb img { width:100%; height:100%; object-fit:cover; }
  .co-oc-info { flex:1; min-width:0; }
  .co-oc-row { display:flex; align-items:center; gap:8px; }
  .co-oc-name { flex:1; min-width:0; font-size:15px; font-weight:600; color:var(--co-ink); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .co-oc-loc { display:flex; align-items:center; gap:6px; margin-top:6px; font-size:12.5px; color:var(--co-muted); min-width:0; }
  .co-oc-loc span:first-of-type { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .co-oc-code { margin-left:auto; flex-shrink:0; font-size:11px; font-weight:600; color:#475467; background:#f2f4f7; border-radius:4px; padding:1px 7px; }
  .co-pill { display:inline-flex; align-items:center; gap:6px; font-size:11.5px; font-weight:600; padding:2px 9px; border-radius:20px; flex-shrink:0; }
  .co-pill::before { content:""; width:6px; height:6px; border-radius:50%; background:currentColor; }
  .co-pill.is-active { background:#e6f7ee; color:#0f8a4c; }
  .co-pill.is-inactive { background:#fff1e0; color:#c2570c; }
  .co-kebab.btn { width:28px; height:28px; padding:0; display:inline-flex; align-items:center; justify-content:center; color:#475467; border-radius:6px; border:0; }
  .co-kebab.btn::after { display:none; }
  .co-kebab.btn:hover, .co-kebab.btn:focus, .show > .co-kebab.btn { background:#f2f4f7; color:var(--co-ink); box-shadow:none; }
  .co-menu { font-size:13px; border-radius:8px; border-color:var(--co-line); box-shadow:0 8px 24px rgba(16,24,40,.12); min-width:170px; }
  .co-menu .dropdown-item { display:flex; align-items:center; gap:9px; padding:7px 14px; }

  .co-oc-tiles { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:4px; margin-top:12px; background:#f6f7fb; border-radius:8px; padding:10px 8px; }
  .co-oc-tile { display:flex; align-items:flex-start; gap:6px; min-width:0; }
  .co-oc-tile > i { color:var(--co-primary); font-size:15px; margin-top:2px; }
  .co-oc-tile-label { font-size:11px; color:var(--co-muted); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .co-oc-tile-value { font-size:14px; font-weight:700; color:var(--co-ink); line-height:1.2; }
  .co-oc-toggle { width:100%; display:flex; align-items:center; gap:8px; margin-top:10px; border:0; background:#f6f7fb; border-radius:8px; padding:8px 12px; font-size:12.5px; font-weight:500; color:#344054; text-align:left; }
  .co-oc-toggle:hover { background:#eef1f8; color:var(--co-primary); }

  /* Details (staff / assignments / tasks) */
  .co-details { display:flex; flex-direction:column; gap:10px; margin-top:10px; }
  .co-details.is-wide { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); margin:6px 0; }
  .co-section { border:1px solid var(--co-line); border-radius:8px; background:#fff; padding:10px 12px; }
  .co-section-head { display:flex; justify-content:space-between; align-items:center; font-size:11px; font-weight:700; color:#475467; text-transform:uppercase; letter-spacing:.07em; margin-bottom:8px; }
  .co-section-head-actions { display:flex; align-items:center; gap:6px; }
  .co-section-head button, .co-link { background:none; border:0; padding:0; font-size:12px; font-weight:600; text-transform:none; letter-spacing:normal; color:var(--co-primary); cursor:pointer; }
  .co-section-head button:hover, .co-link:hover { color:var(--co-primary-dark); text-decoration:underline; }
  .co-sep { color:#d0d5dd; font-size:12px; }
  .co-section-count { font-weight:600; color:#98a2b3; margin-left:6px; font-size:10.5px; }
  .co-section-list { max-height:110px; overflow-y:auto; display:flex; flex-direction:column; gap:6px; scrollbar-width:thin; scrollbar-color:#b0b0b0 transparent; }
  .co-section-list-tall { max-height:150px; }
  .co-section-row { display:flex; align-items:center; gap:8px; font-size:12.5px; color:#344054; }
  .co-section-row span:first-child { flex:1 1 auto; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-weight:500; }
  .co-section-sub { color:var(--co-muted); white-space:nowrap; flex-shrink:0; font-size:11.5px; }
  .co-row-delete { background:none; border:0; padding:0; width:18px; flex-shrink:0; font-size:16px; line-height:1; color:#b6bcc7; text-align:center; }
  .co-row-delete:hover { color:#d92d20; }
  .co-section-empty { font-size:12px; color:#98a2b3; }

  /* Tables */
  .co-tablewrap { overflow-x:auto; border:1px solid var(--co-line); border-radius:8px; }
  .co-table { width:100%; border-collapse:collapse; font-size:13.5px; }
  .co-table thead th { background:#f9fafb; font-size:11px; text-transform:uppercase; letter-spacing:.05em; color:#667085; font-weight:600; padding:10px 14px; border-bottom:1px solid var(--co-line); white-space:nowrap; }
  .co-table td { padding:10px 14px; color:#344054; border-bottom:1px solid #f0f2f5; vertical-align:middle; }
  .co-table tbody tr:last-child td { border-bottom:0; }
  .co-table tbody tr:hover td { background:#fafbff; }
  .co-detail-row td { background:#fafbff !important; padding:6px 14px 10px; }
  .co-cell-outlet { display:flex; align-items:center; gap:10px; }
  .co-cell-name { font-weight:600; color:var(--co-ink); }
  .co-cell-sub { font-size:11.5px; color:var(--co-muted); }
  .co-tag { display:inline-block; font-size:11px; font-weight:600; color:var(--co-primary); background:#eef2ff; border-radius:4px; padding:2px 8px; letter-spacing:.02em; }
  .co-tag.is-soft { color:#475467; background:#f2f4f7; }
  .co-ibtns { display:inline-flex; gap:2px; }
  .co-ibtn { width:30px; height:30px; border:0; background:none; border-radius:6px; color:#667085; display:inline-flex; align-items:center; justify-content:center; font-size:15px; }
  .co-ibtn:hover { background:#eef2ff; color:var(--co-primary); }
  .co-ibtn.is-danger:hover { background:#fee4e2; color:#d92d20; }

  .co-empty-block { text-align:center; padding:44px 0; color:var(--co-muted); font-size:13.5px; }
  .co-empty-icon { width:54px; height:54px; margin:0 auto 12px; border-radius:12px; background:#eef2ff; color:var(--co-primary); display:flex; align-items:center; justify-content:center; font-size:24px; }
  .co-empty-title { margin-bottom:12px; font-weight:500; }

  .co-pagination { display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; margin-top:16px; padding-top:14px; border-top:1px solid #edeef0; }
  .co-pagination-info { font-size:12.5px; color:#8a8f98; }
  .co-pagination-controls { display:flex; align-items:center; gap:10px; }
  .co-pagination-page { font-size:12.5px; color:#4b5563; }

  /* Modals + forms */
  .co-modal .modal-content { border:0; border-radius:12px; box-shadow:0 20px 50px rgba(16,24,40,.25); }
  .co-modal .modal-header { align-items:center; gap:12px; padding:16px 20px; border-bottom:1px solid var(--co-line); }
  .co-modal .modal-header .btn-close { margin:0 0 0 auto; }
  .co-mh-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
  .co-modal-title { font-size:16px; font-weight:700; color:var(--co-ink); text-transform:capitalize; }
  .co-modal-sub { font-size:12.5px; color:var(--co-muted); margin-top:1px; }
  .co-modal .modal-body { padding:18px 20px; max-height:calc(100vh - 220px); overflow-y:auto; }
  .co-modal .modal-footer { gap:8px; padding:12px 20px; border-top:1px solid var(--co-line); background:#fafbfc; border-radius:0 0 12px 12px; }
  .co-foot-hint { margin-right:auto; font-size:11.5px; color:#98a2b3; }
  .co-foot-hint kbd { background:#fff; border:1px solid #d0d5dd; border-bottom-width:2px; border-radius:4px; color:#475467; font-size:10.5px; padding:0 5px; }
  .co-prefill { background:#f5f7ff; border:1px dashed #c7d2fe; border-radius:8px; padding:12px 14px; margin-bottom:16px; }
  .co-prefill-title { font-size:12.5px; font-weight:600; color:#3a57e8; margin-bottom:8px; }
  .co-fsec { display:flex; flex-direction:column; gap:14px; }
  .co-fsec + .co-fsec { margin-top:20px; padding-top:16px; border-top:1px solid #f0f2f5; }
  .co-fsec-title { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.08em; color:#667085; margin-bottom:-2px; }
  .co-grid { display:grid; gap:14px; }
  .co-grid-2 { grid-template-columns:repeat(2,minmax(0,1fr)); }
  .co-grid-3 { grid-template-columns:repeat(3,minmax(0,1fr)); }
  .co-span-2 { grid-column:span 2; }
  .co-field { min-width:0; }
  .co-label { display:block; font-size:12.5px; font-weight:600; color:#344054; margin-bottom:5px; }
  .co-req { color:#d92d20; margin-left:3px; }
  .co-req-note { margin-top:14px; font-size:11.5px; color:#98a2b3; }
  .co-field .form-control { height:38px; font-size:14px; border-radius:8px; border-color:#d0d5dd; }
  .co-field textarea.form-control { height:auto; }
  .co-field .form-control:focus { border-color:var(--co-primary); box-shadow:0 0 0 3px rgba(58,87,232,.15); }
  .co-field .form-control.is-invalid { border-color:#dc3545; background-image:none; padding-right:12px; }
  .co-narrow { max-width:200px; }
  .co-err { margin-top:5px; font-size:12px; color:#d92d20; display:flex; align-items:center; gap:5px; }
  .co-warn { margin-top:5px; font-size:12px; color:#b54708; display:flex; align-items:center; gap:5px; }
  .co-hint-txt { margin-top:5px; font-size:11.5px; color:#98a2b3; }
  .co-coords .co-field:last-child .co-label { visibility:hidden; }

  .co-seg { display:inline-flex; flex-wrap:wrap; gap:6px; }
  .co-seg button { display:inline-flex; align-items:center; gap:6px; height:36px; padding:0 14px; font-size:13px; font-weight:500; color:#475467; background:#fff; border:1px solid #d0d5dd; border-radius:8px; transition:all .12s; }
  .co-seg button:hover { border-color:#98a2b3; }
  .co-seg button.is-on { background:#eef2ff; border-color:var(--co-primary); color:var(--co-primary); font-weight:600; box-shadow:0 0 0 1px var(--co-primary) inset; }

  .co-opt { display:flex; flex-direction:column; line-height:1.25; }
  .co-opt small { color:#98a2b3; font-size:11.5px; }

  .co-delete-modal .modal-body { padding:24px 22px 20px; }
  .co-del-icon { width:46px; height:46px; margin:0 auto 12px; border-radius:50%; background:#fee4e2; color:#d92d20; display:flex; align-items:center; justify-content:center; font-size:20px; }
  .co-delete-text { font-size:13px; color:#475467; margin:0 0 16px; line-height:1.5; }

  @media (max-width: 1399px) { .co-outlet-grid { grid-template-columns:repeat(2,minmax(0,1fr)); } .co-details.is-wide { grid-template-columns:1fr; } }
  @media (max-width: 1199px) { .co-stats { grid-template-columns:repeat(3,minmax(0,1fr)); } .co-banner-art { display:none; } }
  @media (max-width: 991px) { .co-outlet-grid { grid-template-columns:1fr; } .co-page { padding:14px 12px 32px; } }
  @media (max-width: 767px) {
    .co-stats { grid-template-columns:repeat(2,minmax(0,1fr)); }
    .co-banner { flex-wrap:wrap; }
    .co-searchbox, .co-select.form-select { width:100%; }
    .co-filters { width:100%; }
    .co-grid-2, .co-grid-3 { grid-template-columns:1fr; }
    .co-span-2 { grid-column:auto; }
    .co-foot-hint { display:none; }
    .co-narrow { max-width:none; }
  }
`;

export default ChecklistOperations;